import DuplicateActionModalWithoutRecurrence from '@/components/DuplicateActionModalWithoutRecurrence';
import StyledModal from '@/components/StyledModal';
import { WEEKDAYS } from '@/constants/calendar';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { INSERT_ACTIONS } from '@/gql/actions';
import { CreateActionsResponse, QuickCreateActionPayload } from '@/gql/actions/types';
import { CREATE_EVENTS } from '@/gql/events';
import { CreateEventsPayload, CreateEventsResponse } from '@/gql/events/types';
import { GET_OR_CREATE_WEEKLY_PLANS_BATCH } from '@/gql/weeklyPlan';
import { GetOrCreateWeeklyPlansResponse } from '@/gql/weeklyPlan/types';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import { useCronofySyncEvent } from '@/services/cronofy/hooks';
import { fetchData } from '@/services/graphql';
import { useUserInfo } from '@/services/user/hooks';
import { Action } from '@/types/actions';
import { UserGroup } from '@/types/user';
import { createActionDuplicate, refetchActions } from '@/utils/action';
import {
  formatDateToString,
  getRepeatDatesFromSchedule,
  getWeekBoundariesForDates,
  startOfTheWeek,
  utcToLocalDate,
} from '@/utils/calendar';
import rem from '@/utils/rem';
import { Button, HStack, ModalBody, ModalFooter, Text, VStack, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

export type DuplicateActionScheduleType = {
  repeatTimes: number;
  intervalType: number;
  onWeekdays: string[];
};

type Props = {
  action: Action;
  isOpen: boolean;
  onCloseDuplicateActionModal: () => void;
};

const SimpleDuplicateActionModal = ({ action, isOpen, onCloseDuplicateActionModal }: Props) => {
  const { t } = useTranslation('DuplicateActionModal');
  const [isConfirmRepeatModal, setIsConfirmRepeatModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const actionDayOfWeekIndex = useMemo(() => {
    if (action.scheduledDate)
      return utcToLocalDate(action.scheduledDate, action.scheduledTime, action.timezone, action.gmtOffset).getDay();
    return 0;
  }, [action.scheduledDate, action.scheduledTime]);

  const actionDayOfWeek = useMemo(() => WEEKDAYS[actionDayOfWeekIndex], [actionDayOfWeekIndex]);

  const defaultRepeatSchedule = useMemo(
    () => ({
      repeatTimes: 1,
      intervalType: 0,
      onWeekdays: [actionDayOfWeek],
    }),
    [actionDayOfWeek],
  );

  const [repeatSchedule, setRepeatSchedule] = useState<DuplicateActionScheduleType>(defaultRepeatSchedule);

  const toast = useToast();
  const { mutate: cronofySyncEvent } = useCronofySyncEvent();

  useEffect(() => {
    if (repeatSchedule.intervalType === 0) {
      setRepeatSchedule((schedule) => ({ ...schedule, onWeekdays: [actionDayOfWeek] }));
    }
  }, [repeatSchedule.intervalType, actionDayOfWeek]);

  useEffect(() => {
    setRepeatSchedule((schedule) => ({ ...schedule, onWeekdays: [actionDayOfWeek] }));
  }, [actionDayOfWeek]);

  const handleCloseConfirmRepeatModal = useCallback(() => {
    setIsConfirmRepeatModal(false);
  }, [setIsConfirmRepeatModal]);

  const syncWithCronofy = useCallback(
    (actionId: string) => {
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          cronofySyncEvent({ eventId: actionId, eventType: 'Action' });
          resolve();
        }, 100);
      });
    },
    [cronofySyncEvent],
  );

  const handleDuplicateAction = useCallback(async () => {
    if (repeatSchedule && action?.scheduledDate) {
      setIsLoading(true);
      const repeatOnDates = getRepeatDatesFromSchedule(
        repeatSchedule,
        action.scheduledDate,
        action.scheduledTime,
        action.timezone,
        action.gmtOffset,
      );
      const weekBoundariesForDates: [Date, Date][] = getWeekBoundariesForDates(repeatOnDates);

      try {
        const weeklyPlans: Record<string, string> = {};
        const weeklyPlansData = await fetchData<GetOrCreateWeeklyPlansResponse>(GET_OR_CREATE_WEEKLY_PLANS_BATCH, {
          objects: weekBoundariesForDates.map(([dateFrom, dateTo]) => ({
            dateFrom: formatDateToString(dateFrom),
            dateTo: formatDateToString(dateTo),
          })),
        });
        weeklyPlansData.getOrCreateWeeklyPlans.plans.forEach((weeklyPlan) => {
          weeklyPlans[weeklyPlan.dateFrom] = weeklyPlan.id;
        });

        const actions = repeatOnDates
          .map((dateStruct) => {
            const date = utcToLocalDate(
              dateStruct.scheduledDate,
              action.scheduledTime,
              action.timezone,
              action.gmtOffset,
            );
            const weeklyPlanId = weeklyPlans[formatDateToString(startOfTheWeek(date))];
            if (!weeklyPlanId) {
              console.error('Weekly plan ID not found for date', dateStruct);
              return;
            }
            return createActionDuplicate(action, dateStruct.scheduledDate, action.scheduledTime, weeklyPlanId);
          })
          .filter((action) => !!action);

        const insertActionsData = await fetchData<CreateActionsResponse>(INSERT_ACTIONS, {
          objects: actions as QuickCreateActionPayload[],
        });
        const eventsPayload: CreateEventsPayload = { objects: [] };
        for (const action of insertActionsData.insertAction.returning) {
          if (action.scheduledDate) {
            eventsPayload.objects.push({
              id: uuidv4(),
              actionId: action.id,
            });
          }
        }
        const insertEventsData = await fetchData<CreateEventsResponse>(CREATE_EVENTS, eventsPayload);
        insertActionsData.insertAction.returning.forEach(async (action) => {
          const actionEvent = insertEventsData.insertEvent.returning.find((event) => event.action.id === action.id);
          if (actionEvent) {
            action.event = {
              id: actionEvent.id,
              typeName: 'Event',
            };
          }
        });

        for (const action of insertActionsData.insertAction.returning) {
          if (action.scheduledDate && action.scheduledTime) await syncWithCronofy(action.id);
        }

        refetchActions(action);

        toast({
          title: t('toasts.success'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setRepeatSchedule(defaultRepeatSchedule);
        onCloseDuplicateActionModal();
      } catch (error) {
        console.error(error);
        toast({
          title: t('toasts.error'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      setIsLoading(false);
    }
  }, [action, defaultRepeatSchedule, repeatSchedule, toast, syncWithCronofy, onCloseDuplicateActionModal]);

  return (
    <>
      <StyledModal isOpen={isOpen} onClose={onCloseDuplicateActionModal} title={t('title.duplicateAction')} size="xl">
        <ModalBody minHeight={rem(80)} paddingTop={rem(16)}>
          <VStack alignItems="stretch" justifyContent="space-between" gap={rem(12)}>
            <HStack alignItems="baseline">
              <Text>{t('messages.confirmDuplicate')}</Text>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter justifyContent="flex-end" gap={rem(16)} display="flex">
          <HStack gap={rem(16)}>
            <Button onClick={onCloseDuplicateActionModal} size="lg" variant="secondary">
              {t('buttons.cancel')}
            </Button>
            <Button
              isLoading={isLoading}
              onClick={() => {
                handleDuplicateAction();
                handleCloseConfirmRepeatModal();
              }}
              size="lg"
              type="submit"
              variant="primary"
            >
              {t('buttons.confirmDuplicateAction')}
            </Button>
          </HStack>
        </ModalFooter>
      </StyledModal>
    </>
  );
};

const DuplicateActionModal = (props: Props) => {
  const { isEnabled: isEnabledForBeta } = useFeatureFlag(FEATURE_FLAGS.SHOW_RECURRENT_EVENTS_FOR_BETA_USERS);
  const { isEnabled: isEnabledForAll } = useFeatureFlag(FEATURE_FLAGS.SHOW_RECURRENT_EVENTS_FOR_ALL_USERS);

  const { data: userInfo } = useUserInfo();
  const isBetaUser = userInfo?.me?.userGroup === UserGroup.BETA || userInfo?.me?.userGroup === UserGroup.ADMIN;

  const shouldShowFeature = isEnabledForAll || (isEnabledForBeta && isBetaUser);

  return shouldShowFeature ? (
    //TODO:At the moment to delete the flags (SHOW_RECURRENT_EVENTS_FOR_ALL_USERS; SHOW_RECURRENT_EVENTS_FOR_BETA_USERS) we should show this modal only, deleting DuplicateActionModalWithoutRecurrence
    <SimpleDuplicateActionModal {...props} />
  ) : (
    <DuplicateActionModalWithoutRecurrence {...props} />
  );
};

export default DuplicateActionModal;
